// Generated by TaroBase SDK Generator: 2026-07-15T20:57:35.995Z
// Collection: cartItems/$itemId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * A buyer's shopping cart line items. Since no products collection exists yet, each row stores a self-contained snapshot of the product (name, photo, variant, price in Naira, quantity). Owner-scoped: buyers manage only their own cart.
 */

export interface CartItemsRequest {
  owner: AddressType;
  productId?: string;
  productName: string;
  productPhoto?: string;
  variant?: string;
  price: number | TimeOperation | IncrementOperation | TokenAmount;
  quantity: number | IncrementOperation | TokenAmount;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface CartItemsResponse {
  owner: string;
  productId?: string;
  productName: string;
  productPhoto?: string;
  variant?: string;
  price: number;
  quantity: number;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a CartItems operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildCartItems(itemId: string, data: CartItemsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `cartItems/${itemId}`, document: doc };
}

/**
 * Authenticated buyer only; @newData.owner must equal the caller's wallet. price is a required whole-Naira UInt; quantity is a required Int (frontend should default it to 1). owner is immutable (Address! readonly). createdAt is a Unix-seconds timestamp. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setCartItems(itemId: string, data: CartItemsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`cartItems/${itemId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting CartItems: ${formatError(error)}`);
    return false;
  }
}

export type CartItemsRequestUpdate = Partial<CartItemsRequest>;

/**
 * Build a CartItems update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateCartItems(itemId: string, data: CartItemsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `cartItems/${itemId}`, document: doc };
}

/**
 * Only the owner (owner == caller) may update a cart item (e.g., change quantity or variant). owner cannot change (readonly). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateCartItems(itemId: string, data: CartItemsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`cartItems/${itemId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating CartItems: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Owner-scoped. Only the buyer who owns a cart item (owner == caller) can read it.
   (Get Single Item)
 */
export async function getCartItems(itemId: string): Promise<CartItemsResponse | null> {
  try {
    const result = await get(`cartItems/${itemId}`);
    return result as CartItemsResponse | null;
  } catch (error) {
    console.error(`Error getting CartItems: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single CartItems document. (
  Read Operation Details: Owner-scoped. Only the buyer who owns a cart item (owner == caller) can read it.
  )
 */
export function subscribeCartItems(callback: (data: CartItemsResponse | null) => void, itemId: string): Promise<() => Promise<void>> {
  return subscribe(`cartItems/${itemId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as CartItemsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to CartItems at path '${`cartItems/${itemId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many CartItems items from collection cartItems
 
  Read Operation Details: Owner-scoped. Only the buyer who owns a cart item (owner == caller) can read it.
  
 */
export async function getManyCartItems(filter: string = ""): Promise<CartItemsResponse[]> {
  try {
    const results = await get("cartItems", { prompt: filter });
    return Array.isArray(results) ? results as CartItemsResponse[] : [];
  } catch (error) {
    console.error(`Error getting CartItems collection at path 'cartItems': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in CartItems collection at cartItems
 
  Read Operation Details: Owner-scoped. Only the buyer who owns a cart item (owner == caller) can read it.
  
 */
export function subscribeManyCartItems(callback: (data: CartItemsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("cartItems", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as CartItemsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to CartItems collection at path '${"cartItems"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all CartItems items from collection cartItems
 
  Read Operation Details: Owner-scoped. Only the buyer who owns a cart item (owner == caller) can read it.
  
 */
export async function getAllCartItems(filter: string = ""): Promise<CartItemsResponse[]> {
  try {
    const results = await get("cartItems", { prompt: filter });
    return Array.isArray(results) ? results as CartItemsResponse[] : [];
  } catch (error) {
    console.error(`Error getting CartItems collection at path 'cartItems': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in CartItems collection at cartItems
 
  Read Operation Details: Owner-scoped. Only the buyer who owns a cart item (owner == caller) can read it.
  
 */
export function subscribeAllCartItems(callback: (data: CartItemsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("cartItems", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as CartItemsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to CartItems collection at path '${"cartItems"}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Only the owner (owner == caller) may delete a cart item.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteCartItems(itemId: string): Promise<boolean> {
  try {
    const succeeded = await set(`cartItems/${itemId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting CartItems: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for CartItems for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteCartItems(itemId: string): DocumentOperation {
  return { path: `cartItems/${itemId}`, document: null };
}

